import { useEffect, useState, useCallback, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { Search, Download, Plus, ChevronLeft, ChevronRight, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { message } from 'antd';

import { PlanPackage, PlanFormData, PlanPhase } from './types';
import { initialFormData } from './mockData';
import {
    StatsCards,
    PlanTable,
    PlanFormModal,
    PlanDetailModal,
    DeleteConfirmModal,
    MiniPackageTab,
} from './components';
import { fetchPackageAdminList } from "@/api/Admin/userPackage";
import { fetchAllPackageSummary } from "@/api/Admin/adminPackage";

export default function PlanPackageManagement() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = searchParams.get('tab') === 'mini' ? 'mini' : 'main';
    const setActiveTab = (tab: 'main' | 'mini') => setSearchParams(tab === 'mini' ? { tab: 'mini' } : {}, { replace: true });

    // Data state
    const [plans, setPlans] = useState<PlanPackage[]>([]);
    const [filteredPlans, setFilteredPlans] = useState<PlanPackage[]>([]);

    // Pagination & filter
    const [page, setPage] = useState(1);
    const pageSize = 10;
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [loading, setLoading] = useState(false);
    const [summaryStats, setSummaryStats] = useState({
        total_plans: 0,
        active_plans: 0,
        total_investment: 0,
        total_investors: 0,
    });

    // Modal states
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState<PlanPackage | null>(null);
    const [isEditing, setIsEditing] = useState(false);

    // Form state
    const [formData, setFormData] = useState<PlanFormData>(initialFormData);
    const [formLoading, setFormLoading] = useState(false);

    const isMiniPackage = useCallback((item: any) => item?.parent != null || item?.type === 'mini', []);

    // Load mock data
    useEffect(() => {
        setLoading(true);
        fetchPackageAdminList().then(data => {
            const plansFromAPI: PlanPackage[] = data.data.data
                .filter((item: any) => !isMiniPackage(item))
                .map((item: any) => ({
                id: item.id,
                name: item.name,
                description: item.description,
                price: item.price,
                total_slots: item.total_slots,
                sold_slots: item.sold_slots,
                remaining_slots: item.remaining_slots,
                monthly_commission_rate: item.monthly_commission_rate,
                commission_rate: item.commission_rate,
                commission_start_month: item.commission_start_month,
                duration_months: item.duration_months,
                min_profit_rate: item.min_profit_rate,
                is_active: item.is_active,
                created_at: item.created_at,
                updated_at: item.updated_at,
                image: item.image,
                part_per_user: item.part_per_user,
                high_profit_rate: item.high_profit_rate,
                high_rate_duration: item.high_rate_duration,
                tax_rate: item.tax_rate,
            }));
            setPlans(plansFromAPI);
            setFilteredPlans(plansFromAPI);
        })
            .catch(error => {
                console.error('Error fetching package list:', error);
            })
            .finally(() => setLoading(false));
    }, [isMiniPackage]);

    // Filter plans based on search and status
    useEffect(() => {
        let result = [...plans];

        if (searchTerm) {
            result = result.filter(plan =>
                plan.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        if (statusFilter) {
            const isActive = statusFilter === 'active';
            result = result.filter(plan => plan.is_active === isActive);
        }

        setFilteredPlans(result);
        setPage(1);
    }, [plans, searchTerm, statusFilter]);

    // Pagination
    const paginatedPlans = filteredPlans.slice((page - 1) * pageSize, page * pageSize);
    const totalItems = filteredPlans.length;

    const stats = useMemo(() => {
        const totalPlans = plans.length;
        const activePlans = plans.filter(plan => plan.is_active).length;
        const totalInvestment = plans.reduce((sum, plan) => {
            const packagePrice = Number(plan.price) || 0;
            return sum + packagePrice * (plan.total_slots || 0);
        }, 0);
        const totalInvestors = plans.reduce((sum, plan) => {
            return sum + (plan.sold_slots ?? plan.used_slots ?? 0);
        }, 0);

        return {
            total_plans: totalPlans,
            active_plans: activePlans,
            total_investment: totalInvestment,
            total_investors: totalInvestors,
        };
    }, [plans]);

    useEffect(() => {
        fetchAllPackageSummary()
            .then((summary) => {
                setSummaryStats({
                    total_plans: Number(summary?.data?.total_packages || 0),
                    active_plans: Number(summary?.data?.total_active_packages || 0),
                    total_investment: Number(summary?.data?.total_capital || 0),
                    total_investors: Number(summary?.data?.total_investors || 0),
                });
            })
            .catch(() => {
                setSummaryStats(stats);
            });
    }, [stats]);

    // Form handlers
    const handleInputChange = useCallback((field: keyof PlanFormData, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleAddPhase = useCallback(() => {
        const newPhase: Omit<PlanPhase, 'id'> = {
            phase_number: formData.phases.length + 1,
            title: `${t('plan.form.phase')} ${formData.phases.length + 1}`,
            duration: '',
            description: '',
        };
        setFormData(prev => ({ ...prev, phases: [...prev.phases, newPhase] }));
    }, [formData.phases.length, t]);

    const handleUpdatePhase = useCallback((index: number, field: keyof Omit<PlanPhase, 'id'>, value: any) => {
        setFormData(prev => {
            const newPhases = [...prev.phases];
            newPhases[index] = { ...newPhases[index], [field]: value };
            return { ...prev, phases: newPhases };
        });
    }, []);

    const handleRemovePhase = useCallback((index: number) => {
        setFormData(prev => ({
            ...prev,
            phases: prev.phases.filter((_, i) => i !== index).map((p, i) => ({ ...p, phase_number: i + 1 }))
        }));
    }, []);

    const handleAddBenefit = useCallback(() => {
        setFormData(prev => ({ ...prev, benefits: [...prev.benefits, { description: '' }] }));
    }, []);

    const handleUpdateBenefit = useCallback((index: number, value: string) => {
        setFormData(prev => {
            const newBenefits = [...prev.benefits];
            newBenefits[index] = { description: value };
            return { ...prev, benefits: newBenefits };
        });
    }, []);

    const handleRemoveBenefit = useCallback((index: number) => {
        setFormData(prev => ({
            ...prev,
            benefits: prev.benefits.filter((_, i) => i !== index)
        }));
    }, []);

    const handleImageChange = useCallback((file: File | null, preview: string | null) => {
        setFormData(prev => ({ ...prev, image: file, image_preview: preview }));
    }, []);

    const resetForm = useCallback(() => {
        setFormData(initialFormData);
        setIsEditing(false);
        setSelectedPlan(null);
    }, []);

    // Modal handlers
    const handleOpenCreate = () => {
        resetForm();
        setIsFormModalOpen(true);
    };

    const handleOpenEdit = (plan: PlanPackage) => {
        navigate(`/admin/plan/${plan.id}/edit`);
    };

    const handleViewDetail = (plan: PlanPackage) => {
        setSelectedPlan(plan);
        setIsDetailModalOpen(true);
    };

    const handleOpenDelete = (plan: PlanPackage) => {
        setSelectedPlan(plan);
        setIsDeleteModalOpen(true);
    };

    const handleCloseFormModal = () => {
        setIsFormModalOpen(false);
        resetForm();
    };

    const handleCloseDetailModal = () => {
        setIsDetailModalOpen(false);
        setSelectedPlan(null);
    };

    const handleCloseDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setSelectedPlan(null);
    };

    const handleSubmit = async () => {
        if (!formData.name.trim()) {
            message.warning(t('plan.nameRequired'));
            return;
        }

        setFormLoading(true);
        // giả lập API call
        setTimeout(() => {
            if (isEditing && selectedPlan) {
                // Update existing plan
                setPlans(prev => prev.map(p =>
                    p.id === selectedPlan.id
                        ? {
                            ...p,
                            name: formData.name,
                            description: formData.description,
                            price: formData.price.toString(),
                            total_slots: formData.total_slots,
                            monthly_commission_rate: formData.monthly_commission_rate.toString(),
                            commission_rate: formData.commission_rate.toString(),
                            commission_start_month: formData.commission_start_month,
                            duration_months: formData.duration_months,
                            min_profit_rate: formData.min_profit_rate.toString(),
                            is_active: formData.is_active,
                            part_per_user: formData.part_per_user,
                            high_profit_rate: formData.high_profit_rate.toString(),
                            high_rate_duration: formData.high_rate_duration,
                            tax_rate: formData.tax_rate.toString(),
                            phases: formData.phases.map((phase, i) => ({ ...phase, id: i + 1 })),
                            benefits: formData.benefits.map((b, i) => ({ ...b, id: i + 1 })),
                            updated_at: new Date().toISOString(),
                        }
                        : p
                ));
                message.success(t('plan.updateSuccess'));
            } else {
                // Create new plan
                const newPlan: PlanPackage = {
                    id: Math.max(...plans.map(p => p.id)) + 1,
                    name: formData.name,
                    description: formData.description,
                    price: formData.price.toString(),
                    total_slots: formData.total_slots,
                    used_slots: 0,
                    monthly_commission_rate: formData.monthly_commission_rate.toString(),
                    commission_rate: formData.commission_rate.toString(),
                    commission_start_month: formData.commission_start_month,
                    duration_months: formData.duration_months,
                    min_profit_rate: formData.min_profit_rate.toString(),
                    is_active: formData.is_active,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                    image: null,
                    part_per_user: formData.part_per_user,
                    high_profit_rate: formData.high_profit_rate.toString(),
                    high_rate_duration: formData.high_rate_duration,
                    tax_rate: formData.tax_rate.toString(),
                    phases: formData.phases.map((phase, i) => ({ ...phase, id: i + 1 })),
                    benefits: formData.benefits.map((b, i) => ({ ...b, id: i + 1 })),
                };
                setPlans(prev => [...prev, newPlan]);
                message.success(t('plan.createSuccess'));
            }

            setFormLoading(false);
            handleCloseFormModal();
        }, 500);
    };

    const handleDelete = () => {
        if (!selectedPlan) return;

        // Simulate API call
        setPlans(prev => prev.filter(p => p.id !== selectedPlan.id));
        message.success(t('plan.deleteSuccess'));
        handleCloseDeleteModal();
    };

    const handleEditFromDetail = () => {
        if (selectedPlan) {
            setIsDetailModalOpen(false);
            handleOpenEdit(selectedPlan);
        }
    };

    return (
        <div className="dark:bg-gray-900 min-h-screen font-sans">
            <Helmet>
                <title>{t('plan.title')}</title>
            </Helmet>

            {/* Thêm tabs gói mini */}
            <div className="flex gap-1 mb-6 border-b border-gray-200 dark:border-gray-700">
                <button
                    onClick={() => setActiveTab('main')}
                    className={`px-5 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                        activeTab === 'main'
                            ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
                            : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                >
                    {t('plan.tabMain', 'Gói đầu tư')}
                </button>
                <button
                    onClick={() => setActiveTab('mini')}
                    className={`px-5 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                        activeTab === 'mini'
                            ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
                            : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                >
                    {t('plan.tabMini', 'Gói mini')}
                </button>
            </div>

            {/* Mini tab */}
            {activeTab === 'mini' && <MiniPackageTab />}

            {/* tab gói chính */}
            {activeTab === 'main' && <>

            {/* Header */}
            <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('plan.title')}</h1>
                    <p className="text-gray-500 dark:text-gray-400 mt-1">{t('plan.subtitle')}</p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={() => navigate('split-package')}
                        className="flex items-center justify-center px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md transition-colors font-medium text-sm shadow-sm"
                    >
                        <FileText className="h-4 w-4 mr-2" />
                        {t('plan.splitPackage')}
                    </button>
                    <button
                        onClick={handleOpenCreate}
                        className="flex items-center justify-center px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors font-medium text-sm shadow-sm"
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        {t('plan.addNew')}
                    </button>
                </div>
            </div>

            <StatsCards stats={summaryStats.total_plans > 0 ? summaryStats : stats} />

            <div className="bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="p-5 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="relative flex-1 max-w-md">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Search className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                className="block w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-colors"
                                placeholder={t('plan.searchPlaceholder')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-3 sm:space-y-0">
                            <select
                                className="px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <option value="">{t('plan.allStatuses')}</option>
                                <option value="active">{t('plan.statusActive')}</option>
                                <option value="inactive">{t('plan.statusInactive')}</option>
                            </select>

                            <button className="flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-white rounded-md transition-colors font-medium text-sm">
                                <Download className="h-4 w-4 mr-2" />
                                {t('plan.exportExcel')}
                            </button>
                        </div>
                    </div>
                </div>

                <PlanTable
                    plans={paginatedPlans}
                    loading={loading}
                    onView={handleViewDetail}
                    onEdit={handleOpenEdit}
                    onDelete={handleOpenDelete}
                />

                <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/20 rounded-b-md">
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                        <div>
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                                {t('plan.showing')} <span className="font-medium">{totalItems > 0 ? (page - 1) * pageSize + 1 : 0}</span> {t('plan.to')}{' '}
                                <span className="font-medium">{Math.min(page * pageSize, totalItems)}</span> {t('plan.in')}{' '}
                                <span className="font-medium">{totalItems}</span> {t('plan.results')}
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <button
                                disabled={page === 1}
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 dark:text-white"
                            >
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            <button
                                disabled={page * pageSize >= totalItems}
                                onClick={() => setPage(p => p + 1)}
                                className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 dark:text-white"
                            >
                                <ChevronRight className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modals */}
            <PlanFormModal
                isOpen={isFormModalOpen}
                isEditing={isEditing}
                formData={formData}
                formLoading={formLoading}
                onClose={handleCloseFormModal}
                onSubmit={handleSubmit}
                onInputChange={handleInputChange}
                onAddPhase={handleAddPhase}
                onUpdatePhase={handleUpdatePhase}
                onRemovePhase={handleRemovePhase}
                onAddBenefit={handleAddBenefit}
                onUpdateBenefit={handleUpdateBenefit}
                onRemoveBenefit={handleRemoveBenefit}
                onImageChange={handleImageChange}
            />

            <PlanDetailModal
                isOpen={isDetailModalOpen}
                plan={selectedPlan}
                onClose={handleCloseDetailModal}
                onEdit={handleEditFromDetail}
            />

            <DeleteConfirmModal
                isOpen={isDeleteModalOpen}
                plan={selectedPlan}
                onClose={handleCloseDeleteModal}
                onConfirm={handleDelete}
            />
            </> }
        </div>
    );
}
